<script setup lang="ts">
import { computed } from 'vue';
import { FetchTicketResponsePriority, type NestedTicketStatus, type NestedTicketStatusDetail } from 'src/api/models';
import type { AssigneeData } from 'src/services/assignee-service';
import TicketEditResolution from './TicketEditResolution.vue';
import TicketEditDescription from './TicketEditDescription.vue';
import TicketEditStatusSelect from './TicketEditStatusSelect.vue';
import TicketEditPrioritySelect from './TicketEditPrioritySelect.vue';
import TicketEditAssigneeSearch from './TicketEditAssigneeSearch.vue';
import TicketEditSchedulePicker from './TicketEditSchedulePicker.vue';
import TicketEditDurationPicker from './TicketEditDurationPicker.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

const props = defineProps<{
  title?: string;
  description?: string | null;
  shouldValidateTitle?: boolean;
  scheduledDate?: Date;
  expectedDuration?: { hours: number; minutes: number };
  assignee?: AssigneeData | null;
  priority?: FetchTicketResponsePriority;
  status?: NestedTicketStatus | null;
  statusDetail?: NestedTicketStatusDetail | null;
  showStatus?: boolean;
  createdAt?: string;
}>();

defineEmits<{
  'update:title': [title: string];
  'update:description': [value: string];
  'update:scheduledDate': [date: Date | null];
  'update:expectedDuration': [duration: { hours: number; minutes: number }];
  'update:assignee': [value: AssigneeData | null];
  'update:priority': [value: FetchTicketResponsePriority];
  'update:status': [value: NestedTicketStatus | null];
  'update:statusDetail': [value: NestedTicketStatusDetail | null];
}>();

const formattedCreated = computed(() => {
  if (!props.createdAt) return '—';
  return new Date(props.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});
</script>

<template>
  <div class="p-3">
    <TicketEditSectionHeader title="Type" />
    <TicketEditResolution
      :title="title"
      @update:title="$emit('update:title', $event)"
    />
    <p
      v-if="shouldValidateTitle && !title"
      class="text-xs text-red-500 mt-1.5"
    >
      Please select a resolution type
    </p>

    <div class="border-t border-surface-200 dark:border-surface-700 my-5" />

    <TicketEditSectionHeader title="Description" />
    <TicketEditDescription
      :model-value="description"
      @update:model-value="$emit('update:description', $event)"
    />

    <div class="border-t border-surface-200 dark:border-surface-700 my-5" />

    <TicketEditSectionHeader title="Properties" />

    <div class="flex flex-col gap-0.5">
      <TicketEditStatusSelect
        :status="status"
        :status-detail="statusDetail"
        :show-status="showStatus"
        @update:status="$emit('update:status', $event)"
        @update:status-detail="$emit('update:statusDetail', $event)"
      />

      <TicketEditPrioritySelect
        :priority="priority"
        @update:priority="$emit('update:priority', $event)"
      />

      <TicketEditAssigneeSearch
        :assignee="assignee"
        @update:assignee="$emit('update:assignee', $event)"
      />
    </div>

    <div class="border-t border-surface-200 dark:border-surface-700 my-5" />
    <TicketEditSectionHeader title="Schedule" />

    <div class="flex flex-col gap-0.5">
      <TicketEditSchedulePicker
        :scheduled-date="scheduledDate"
        @update:scheduled-date="$emit('update:scheduledDate', $event)"
      />

      <TicketEditDurationPicker
        :expected-duration="expectedDuration"
        @update:expected-duration="$emit('update:expectedDuration', $event)"
      />
    </div>

    <div class="border-t border-surface-200 dark:border-surface-700 my-5" />
    <div class="flex flex-col gap-1 opacity-60">
      <div class="flex items-center justify-between py-1 px-2">
        <span class="text-[11px] text-surface-400">Created</span>
        <span class="text-[11px] text-surface-400">{{ formattedCreated }}</span>
      </div>
    </div>
  </div>
</template>

